import React from "react";
import { CheckCircle2, RefreshCw, Wrench } from "lucide-react";
import estatesService from "../../services/estatesService";

const dateTimeLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
    : "—";

const titleCase = (value) =>
  String(value || "—").replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());

const statusClass = (status) => {
  const value = String(status || "").toUpperCase();
  if (value === "COMPLETED") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (value === "DECLINED") return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-amber-200 bg-amber-50 text-amber-700";
};

const EstatesServiceRequests = ({ serviceDepartment, notify = () => {} }) => {
  const [requests, setRequests] = React.useState([]);
  const [representatives, setRepresentatives] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [selections, setSelections] = React.useState({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [nextRequests, nextRepresentatives] = await Promise.all([
        estatesService.getServiceRequests(serviceDepartment),
        estatesService.getServiceRepresentatives(serviceDepartment),
      ]);
      setRequests(nextRequests);
      setRepresentatives(nextRepresentatives);
    } catch (error) {
      notify(error?.message || "Service requests could not be loaded.", "error");
    } finally {
      setLoading(false);
    }
  }, [notify, serviceDepartment]);

  React.useEffect(() => { load(); }, [load]);

  const assign = async (request) => {
    const representativeId = selections[request.id] || request.assigned_representative_id || "";
    if (!representativeId) {
      notify("Select a responsible representative first.", "error");
      return;
    }
    setBusy(request.id);
    try {
      await estatesService.assignServiceRequest({ id: request.id, representativeId, status: "ASSIGNED" });
      notify(`${request.service_name} assigned to the selected representative.`);
      await load();
    } catch (error) {
      notify(error?.message || "The service request could not be assigned.", "error");
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="rounded-xl border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-700"><Wrench className="h-5 w-5" /></span>
          <div>
            <h2 className="text-lg font-bold text-slate-950">Meeting service requests</h2>
            <p className="mt-1 text-sm text-slate-500">Assign representatives for approved bookings routed to {titleCase(serviceDepartment)}.</p>
          </div>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold text-slate-700"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      <div className="mt-5 overflow-x-auto rounded-xl border">
        <table className="min-w-[980px] w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Meeting</th><th className="px-4 py-3">Schedule</th><th className="px-4 py-3">Requested service</th><th className="px-4 py-3">Representative</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Action</th></tr></thead>
          <tbody className="divide-y">
            {requests.map((request) => (
              <tr key={request.id}>
                <td className="px-4 py-3"><p className="font-semibold text-slate-900">{request.title}</p><p className="text-xs text-slate-500">{request.booking_number} · {request.space_code}</p></td>
                <td className="px-4 py-3 text-xs text-slate-600">{dateTimeLabel(request.starts_at)}<br />→ {dateTimeLabel(request.ends_at)}</td>
                <td className="px-4 py-3"><p className="font-medium text-slate-800">{request.service_name}</p><p className="mt-1 text-xs text-slate-500">{request.details || "No extra details"}</p></td>
                <td className="px-4 py-3">
                  <select value={selections[request.id] || request.assigned_representative_id || ""} onChange={(event) => setSelections({ ...selections, [request.id]: event.target.value })} className="h-9 min-w-56 rounded-lg border border-slate-300 bg-white px-2 text-xs">
                    <option value="">Select department member</option>
                    {representatives.map((person) => <option key={person.id} value={person.id}>{person.full_name} · {titleCase(person.primary_role)}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusClass(request.status)}`}>{titleCase(request.status)}</span></td>
                <td className="px-4 py-3"><button type="button" disabled={busy === request.id} onClick={() => assign(request)} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" />{busy === request.id ? "Assigning…" : "Assign"}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !requests.length && <p className="p-8 text-center text-sm text-slate-500">No approved service requests are waiting for this department.</p>}
      </div>
    </section>
  );
};

export default EstatesServiceRequests;
